import * as THREE from 'three';
import { useEffect } from 'react';

import { MeshEdgesMode } from '../../../../../../main/modelViewers/components/content/ModelViewerEditor/hooks/useModelViewerMeshEdges';
import { useModelViewer } from '../../../../hooks/useModelViewer';

const EDGES_NAME = '__mesh_edges__';

function applyMode(model: THREE.Object3D, mode: MeshEdgesMode) {
  model.traverse((obj) => {
    [...obj.children]
      .filter((c) => c.name === EDGES_NAME)
      .forEach((c) => c.removeFromParent());

    if (!(obj instanceof THREE.Mesh)) return;

    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    mats.forEach((m: THREE.Material) => {
      (m as THREE.MeshStandardMaterial).wireframe = false;
    });
  });

  if (mode === 'edges') {
    model.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const line = new THREE.LineSegments(
        new THREE.EdgesGeometry(obj.geometry, 15),
        new THREE.LineBasicMaterial({ color: 0x000000, depthTest: true })
      );
      line.name = EDGES_NAME;
      obj.add(line);
    });
  }

  if (mode === 'wireframe') {
    model.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m: THREE.Material) => {
        (m as THREE.MeshStandardMaterial).wireframe = true;
      });
    });
  }
}

interface UseModelViewerMeshEdgesHelperOptions {
  mode: MeshEdgesMode;
}

function useModelViewerMeshEdgesHelper({
  mode
}: UseModelViewerMeshEdgesHelperOptions) {
  const { scene, modelViewerEl } = useModelViewer();

  useEffect(() => {
    if (!scene?.model) return;
    applyMode(scene.model, mode);
    scene.queueRender();
  }, [scene, mode]);

  useEffect(() => {
    if (!modelViewerEl) return;

    const handler = () => {
      if (!scene?.model) return;
      applyMode(scene.model, mode);
      scene.queueRender();
    };

    modelViewerEl.addEventListener('load', handler);

    return () => modelViewerEl.removeEventListener('load', handler);
  }, [modelViewerEl, scene, mode]);
}

export default useModelViewerMeshEdgesHelper;
